import { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import { getMe, postLoginToken } from "@/lib/api";
import { loggedIn, selectIsAuthenticated } from "@/store/authSlice";
import { useAppDispatch, useAppSelector } from "@/store";

interface LoginForm {
  username: string;
  password: string;
}

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const isAuth = useAppSelector(selectIsAuthenticated);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Redirect already-logged-in users to the originally-requested URL or root.
  const from = (location.state as LocationState | null)?.from?.pathname ?? "/products";
  if (isAuth) {
    navigate(from, { replace: true });
    return null;
  }

  const onSubmit = async (form: LoginForm) => {
    setServerError(null);
    setSubmitting(true);
    try {
      const tokenResp = await postLoginToken(form.username, form.password);
      // Token response carries is_admin, so we can populate the slice without
      // an extra /me round-trip. We still call /me afterwards as a sanity
      // check (token subject must match form input — server is source of truth).
      dispatch(loggedIn({
        token:   tokenResp.access_token,
        userId:  form.username,
        isAdmin: tokenResp.is_admin,
      }));
      const me = await getMe();
      if (me.user_id !== form.username || me.is_admin !== tokenResp.is_admin) {
        dispatch(loggedIn({
          token:   tokenResp.access_token,
          userId:  me.user_id,
          isAdmin: me.is_admin,
        }));
      }
      navigate(from, { replace: true });
    } catch (err: unknown) {
      // emp_api returns 401 with {detail: "Invalid credentials"} for both
      // missing user, inactive user, and wrong password. Mirror that surface.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? "Login failed. Please try again.";
      setServerError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app text-header">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-line/60 bg-panel p-8 shadow-2xl"
      >
        <h1 className="text-2xl font-semibold tracking-wide">
          QRAFT <span className="text-accent">EMP</span> sign-in
        </h1>

        <div>
          <label className="block text-sm font-medium text-ink-mute">User ID</label>
          <input
            {...register("username", { required: "User ID is required" })}
            type="text"
            autoComplete="username"
            className="mt-1 block w-full rounded-md bg-panel-2 border border-line px-3 py-2 text-ink placeholder:text-ink-dim focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none"
          />
          {errors.username && <p className="mt-1 text-xs text-down">{errors.username.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-ink-mute">Password</label>
          <input
            {...register("password", { required: "Password is required" })}
            type="password"
            autoComplete="current-password"
            className="mt-1 block w-full rounded-md bg-panel-2 border border-line px-3 py-2 text-ink placeholder:text-ink-dim focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none"
          />
          {errors.password && <p className="mt-1 text-xs text-down">{errors.password.message}</p>}
        </div>

        {serverError && (
          <div className="rounded-md bg-down/10 border border-down/30 px-3 py-2 text-sm text-down">
            {serverError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-accent text-white py-2 font-semibold hover:bg-accent/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
